/**
 * RegenerateButton Component V2
 *
 * Button für Channel Package Regeneration mit Animation.
 *
 * Built with Tailwind CSS v4.1
 * WCAG 2.1 Level AA + BFSG konform
 */

import { RefreshCw, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/v2/ui';
import { cn } from '@/lib/utils';

interface RegenerateButtonProps {
  onClick: () => void;
  isRegenerating?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'warning' | 'outline';
  showLabel?: boolean;
  className?: string;
}

export function RegenerateButton({
  onClick,
  isRegenerating = false,
  disabled = false,
  size = 'md',
  variant = 'outline',
  showLabel = true,
  className,
}: RegenerateButtonProps) {
  const { t } = useTranslation();
  const sizeMap = {
    sm: 'sm' as const,
    md: 'md' as const,
    lg: 'lg' as const,
  };

  const variantMap = {
    primary: 'primary' as const,
    secondary: 'secondary' as const,
    warning: 'primary' as const,
    outline: 'outline' as const,
  };

  return (
    <Button
      variant={variantMap[variant]}
      size={sizeMap[size]}
      onClick={onClick}
      disabled={disabled || isRegenerating}
      className={cn(
        'group transition-all duration-200',
        variant === 'warning' && 'bg-amber-500 hover:bg-amber-600 border-amber-500',
        className
      )}
      aria-label={isRegenerating ? t('channelPackages.regenerateRunningAriaLabel') : t('channelPackages.regenerateAriaLabel')}
    >
      <RefreshCw
        className={cn(
          'h-4 w-4',
          isRegenerating && 'animate-spin',
          showLabel && 'mr-2'
        )}
      />
      {showLabel && (
        <span>{isRegenerating ? t('channelPackages.regenerating') : t('channelPackages.regenerate')}</span>
      )}
    </Button>
  );
}

/**
 * Regenerate All Button
 */
interface RegenerateAllButtonProps {
  onClick: () => void;
  isRegenerating?: boolean;
  disabled?: boolean;
  count: number;
  outdatedCount?: number;
  className?: string;
}

export function RegenerateAllButton({
  onClick,
  isRegenerating = false,
  disabled = false,
  count,
  outdatedCount = 0,
  className,
}: RegenerateAllButtonProps) {
  const { t } = useTranslation();
  const hasOutdated = outdatedCount > 0;
  const label = hasOutdated
    ? t('channelPackages.regenerateOutdated', { count: outdatedCount })
    : t('channelPackages.regenerateAll', { count });

  return (
    <Button
      variant={hasOutdated ? 'primary' : 'outline'}
      onClick={onClick}
      disabled={disabled || isRegenerating}
      className={cn(
        'group transition-all duration-200',
        hasOutdated && 'bg-amber-500 hover:bg-amber-600 border-amber-500 text-white',
        className
      )}
    >
      <RefreshCw
        className={cn(
          'h-4 w-4 mr-2',
          isRegenerating && 'animate-spin'
        )}
      />
      <span>{isRegenerating ? t('channelPackages.regenerating') : label}</span>
      {hasOutdated && !isRegenerating && (
        <AlertTriangle className="h-4 w-4 ml-2 text-amber-100" />
      )}
    </Button>
  );
}

/**
 * Regenerate Status Indicator (for inline use)
 */
interface RegenerateStatusProps {
  status: 'pending' | 'processing' | 'completed' | 'error';
  error?: string | null;
  className?: string;
}

export function RegenerateStatus({
  status,
  error,
  className,
}: RegenerateStatusProps) {
  const { t } = useTranslation();
  const statusConfig = {
    pending: {
      icon: Clock,
      textKey: 'channelPackages.statusPending',
      className: 'text-neutral-500',
    },
    processing: {
      icon: RefreshCw,
      textKey: 'channelPackages.regenerating',
      className: 'text-primary-600',
      animate: true,
    },
    completed: {
      icon: CheckCircle,
      textKey: 'channelPackages.statusCompleted',
      className: 'text-emerald-600',
    },
    error: {
      icon: XCircle,
      textKey: 'channelPackages.statusError',
      className: 'text-red-600',
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-sm font-medium',
        config.className,
        className
      )}
      title={status === 'error' ? error || undefined : undefined}
    >
      <Icon
        className={cn(
          'h-4 w-4',
          'animate' in config && config.animate && 'animate-spin'
        )}
      />
      {t(config.textKey)}
    </span>
  );
}
